import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Phone, Menu, X, Search, Ship } from 'lucide-react';
import { Button } from './ui/button';
import { useSettings } from '../context/SettingsContext';

const NAV_LINKS = [
  { label: 'Home', to: '/' },
  { label: 'Nile Cruises', to: '/cruises' },
  { label: 'Egypt Tours', to: '/tours' },
  { label: 'Packages', to: '/packages' },
  { label: 'Travel Guide', to: '/blog' },
  { label: 'About', to: '/about' },
  { label: 'Contact', to: '/contact' },
];

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const settings = useSettings();
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => setOpen(false), [loc.pathname]);

  return (
    <>
      <div className="hidden md:block bg-[#304b71] text-white text-xs">
        <div className="max-w-7xl mx-auto flex justify-between items-center px-6 py-2">
          <span className="opacity-90">Welcome to {settings.brand} — Egypt's Trusted Nile Experts</span>
          <div className="flex items-center gap-5">
            <a href={`tel:${settings.phone_raw}`} className="flex items-center gap-2 hover:text-[#a6b2b5] transition-colors">
              <Phone size={13} /> {settings.phone}
            </a>
            <Link to="/request-quote" className="hover:text-[#a6b2b5] transition-colors">Request a Quote</Link>
          </div>
        </div>
      </div>

      <header className={`sticky top-0 z-40 transition-all duration-300 ${scrolled ? 'bg-white/95 backdrop-blur shadow-md' : 'bg-white'}`}>
        <div className="max-w-7xl mx-auto flex items-center justify-between px-6 py-4">
          <Link to="/" className="flex items-center gap-2 group">
            <div className="w-11 h-11 rounded-full bg-[#304b71] flex items-center justify-center text-white group-hover:bg-[#6a8aa4] transition-colors">
              <Ship size={22} />
            </div>
            <div className="leading-tight">
              <div className="font-serif text-lg text-[#304b71] tracking-wide">{settings.brand}</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#6a8aa4]">Egypt · Since 1998</div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-7">
            {NAV_LINKS.map((l) => {
              const active = loc.pathname === l.to;
              return (
                <Link key={l.to} to={l.to} className={`text-sm font-medium relative after:absolute after:left-0 after:-bottom-1 after:h-[2px] after:bg-[#304b71] after:transition-all ${active ? 'text-[#304b71] after:w-full' : 'text-slate-700 hover:text-[#304b71] after:w-0 hover:after:w-full'}`}>
                  {l.label}
                </Link>
              );
            })}
          </nav>

          <div className="hidden md:flex items-center gap-3">
            <a href={`tel:${settings.phone_raw}`} className="hidden xl:flex items-center gap-2 text-sm text-[#304b71]">
              <Phone size={16} /> <span className="font-semibold">{settings.phone}</span>
            </a>
            <Button asChild className="bg-[#304b71] hover:bg-[#6a8aa4] text-white rounded-full px-5">
              <Link to="/request-quote">Book Now</Link>
            </Button>
          </div>

          <button className="lg:hidden text-[#304b71]" onClick={() => setOpen(!open)} aria-label="Menu">
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>

        {open && (
          <div className="lg:hidden bg-white border-t border-slate-100 px-6 py-4 space-y-2">
            {NAV_LINKS.map((l) => (
              <Link key={l.to} to={l.to} className="block text-slate-700 py-2">{l.label}</Link>
            ))}
            <Link to="/request-quote" className="block">
              <Button className="w-full bg-[#304b71] hover:bg-[#6a8aa4] text-white rounded-full mt-2">Book Now</Button>
            </Link>
          </div>
        )}
      </header>
    </>
  );
};

export default Header;
